'use client'

import { useEffect, useMemo } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useExtracted } from 'next-intl'
import { CreditCard, KeyRound, Link as LinkIcon, Shapes, Tag, User, Waypoints } from '@/components/ui/icons'
import { useAppHeader } from '@/components/AppShell'
import { useBillingInfo } from '@/hooks/useBillingInfo'
import { useByokProviders } from '@/hooks/useByokProviders'
import { cn } from '@/lib/utils'
import { useWorkspaceContext } from '@/store/workspaceContextStore'

function matchesTab(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(href + '/')
}

export default function UserLayout({ children }: { children: React.ReactNode }) {
  const t = useExtracted()
  const router = useRouter()
  const pathname = usePathname()
  const { billingInfo } = useBillingInfo()
  const providerAccess = useByokProviders(billingInfo?.personalOrg?.id ?? null)

  const allTabs = useMemo(
    () => [
      { label: t('Account'), href: '/settings/account', icon: <User className="w-4 h-4" />, id: 'account' },
      { label: t('Billing'), href: '/settings/billing', icon: <CreditCard className="w-4 h-4" />, id: 'billing' },
      { label: 'Martini API', href: '/settings/api', icon: <KeyRound className="w-4 h-4" />, id: 'api' },
      {
        label: 'Providers',
        href: '/settings/connections',
        icon: <Waypoints className="w-4 h-4" />,
        id: 'connections',
      },
      // Named by what's inside (it's the model picker), not "Preferences" /
      // "Settings" — the navbar's Settings tab is the door to this whole page.
      { label: 'Labels', href: '/settings/labels', icon: <Tag className="w-4 h-4" />, id: 'labels' },
      { label: t('Models'), href: '/settings/models', icon: <Shapes className="w-4 h-4" />, id: 'settings' },
      {
        label: t('Partner program'),
        href: '/settings/partners',
        icon: <LinkIcon className="w-4 h-4" />,
        id: 'partners',
      },
    ],
    [t],
  )

  const tabs = allTabs.filter(tab => tab.id !== 'connections' || providerAccess.anyVisible)
  const activeTab = [...tabs].sort((a, b) => b.href.length - a.href.length).find(tab => matchesTab(pathname, tab.href))
  const breadcrumb = activeTab?.label ?? t('Account')
  useAppHeader({ breadcrumbs: [{ label: breadcrumb }] })
  // Context follows the page: /settings/*, /account and /billing are the
  // PERSONAL workspace's admin pages, so the navbar switcher snaps to Personal
  // (symmetric with /team/<slug>). Unconditional, not "only if it differs": on
  // a cold load the store already reads 'personal' (SSR parity), so a guarded
  // call would no-op WITHOUT marking the store hydrated, and the stale
  // persisted team would then hydrate over it a commit later with nothing left
  // to correct it (ENG-727). The workspace surfaces close the same race the
  // other way, by restoring the remembered workspace onto the URL (ENG-725);
  // these pages have no URL to restore into — they are personal by
  // definition — so they just assert the value.
  useEffect(() => {
    useWorkspaceContext.getState().setContext('personal')
  }, [])

  return (
    // Settings grammar: a quiet left rail of sections + a narrow content pane
    // (the Linear/Stripe shape). The navbar above already answers WHERE you
    // are (Settings tab, Personal chip), so no page-level title repeats it.
    <div className="min-h-[calc(100vh-3rem)] bg-background">
      <div className="mx-auto flex max-w-5xl gap-12 px-6 py-10 lg:px-8">
        <nav className="w-44 shrink-0">
          <div className="sticky top-24 space-y-0.5">
            <div className="mb-2 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
              {t('Personal')}
            </div>
            {tabs.map(tab => {
              const isActive = activeTab?.href === tab.href
              return (
                <button
                  key={tab.href}
                  onClick={() => router.push(tab.href)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    isActive
                      ? 'bg-secondary text-foreground'
                      : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
                  )}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>
        </nav>

        <main className="min-w-0 max-w-3xl flex-1">{children}</main>
      </div>
    </div>
  )
}
